import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { messagesOfTask, statusText, type Message, type Task } from '@machs/shared';
import { useSync } from '../lib/sync';
import { usePlan } from '../lib/plan';
import { Icon } from '../lib/icons';
import { avatarGradient, dayLabel, initials } from '../lib/format';
import { userAvatarUrl } from '../lib/api';
import { imageUrl } from '../lib/images';
import { MessageBubble } from './MessageBubble';
import { Composer, type ReplyTarget } from './Composer';
import { DropDialog } from './DropUpload';
import { Avatar } from './ui';

type Members = ReturnType<typeof useSync>['me']['members'];

function memberAvatar(members: Members, userId: string | null, name: string, size: number) {
  const m = userId ? members.find((x) => x.userId === userId) : undefined;
  return <Avatar name={initials(m?.name ?? name)} gradient={avatarGradient(userId ?? name)} src={m ? userAvatarUrl(m.userId, m.avatarAt) : null} size={size} />;
}

export function ChatView({ task, onBack, detailOpen, onToggleDetail, onEditProject, onShare, onOpenTask }: {
  task: Task;
  /** Hauptaufgabe öffnen (Verweis im Kopf einer Unteraufgabe) */
  onOpenTask?: (id: string) => void;
  /** Ohne Zurück-Knopf, wenn es keine Liste gibt (Gast mit Aufgaben-Freigabe) */
  onBack?: () => void;
  detailOpen: boolean;
  onToggleDetail: () => void;
  onEditProject: (projectId: string) => void;
  onShare?: () => void;
}) {
  const { client, me, version } = useSync();
  const plan = usePlan();
  const messages = useMemo(() => messagesOfTask(client, task.id), [client, version, task.id]);
  const project = client.data.project.get(task.projectId);
  const business = project ? client.data.business.get(project.businessId) : undefined;
  const parentTask = task.parentId ? client.data.task.get(task.parentId) : undefined;
  const severalBusinesses = [...client.data.business.values()].filter((b) => !b.archivedAt).length > 1;
  const [reply, setReply] = useState<ReplyTarget | null>(null);
  const [editing, setEditing] = useState<Message | null>(null);
  // Suche in dieser Aufgabe: Nachrichten und Transkripte von Sprache und Video
  const [suche, setSuche] = useState('');
  const [sucheOffen, setSucheOffen] = useState(false);
  const treffer = useMemo(() => {
    const begriff = suche.trim().toLowerCase();
    if (!begriff) return [];
    const gefunden: { id: string; wer: string; text: string; woher: 'nachricht' | 'transkript' }[] = [];
    for (const m of messages) {
      if (m.deletedAt) continue;
      if (m.body.toLowerCase().includes(begriff)) {
        gefunden.push({ id: m.id, wer: m.authorName, text: m.body, woher: 'nachricht' });
        continue;
      }
      const medium = m.mediaId ? client.data.media.get(m.mediaId) : null;
      const zeile = medium?.transcript?.split('\n').find((z) => z.toLowerCase().includes(begriff));
      if (zeile) gefunden.push({ id: m.id, wer: m.authorName, text: zeile, woher: 'transkript' });
      else if (medium?.summary?.toLowerCase().includes(begriff)) {
        gefunden.push({ id: m.id, wer: m.authorName, text: medium.summary, woher: 'transkript' });
      }
    }
    return gefunden.reverse().slice(0, 30);
  }, [suche, messages, client, version]);

  /** Zur Nachricht springen und sie kurz hervorheben. */
  function zeige(id: string) {
    const el = scroller.current?.querySelector(`[data-msg="${id}"]`);
    if (!el) return;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    el.classList.add('flash');
    setTimeout(() => el.classList.remove('flash'), 1600);
  }

  const scroller = useRef<HTMLDivElement>(null);
  const stick = useRef(true);

  // Wie WhatsApp: unten beginnen und bei neuen Nachrichten unten bleiben
  useEffect(() => {
    const el = scroller.current;
    if (el && stick.current) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  // Lesestand setzen
  const last = messages.at(-1);
  useEffect(() => {
    if (last && last.authorId !== me.user.id) void client.setTaskUserState(task.id, { lastReadAt: last.createdAt });
  }, [client, task.id, last?.id, last?.authorId, last?.createdAt, me.user.id]);

  const done = task.status === 'erledigt';
  const guest = me.guest;
  const darfAblegen = !guest || guest.canWrite;
  const [ziehen, setZiehen] = useState(false);
  const [abgelegt, setAbgelegt] = useState<File[] | null>(null);

  // Dateien aus dem Finder/Explorer in die Aufgabe ziehen, auch mehrere auf einmal
  useEffect(() => {
    const flaeche = scroller.current?.parentElement;
    if (!flaeche || !darfAblegen) return;
    let tiefe = 0;
    const mitDateien = (e: DragEvent) => !!e.dataTransfer && [...e.dataTransfer.types].includes('Files');
    const enter = (e: DragEvent) => {
      if (!mitDateien(e)) return;
      e.preventDefault();
      tiefe++;
      setZiehen(true);
    };
    const over = (e: DragEvent) => {
      if (!mitDateien(e)) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    };
    const leave = (e: DragEvent) => {
      if (!mitDateien(e)) return;
      tiefe = Math.max(0, tiefe - 1);
      if (tiefe === 0) setZiehen(false);
    };
    const drop = (e: DragEvent) => {
      if (!mitDateien(e)) return;
      e.preventDefault();
      tiefe = 0;
      setZiehen(false);
      const dateien = [...(e.dataTransfer?.files ?? [])];
      if (dateien.length) setAbgelegt(dateien);
    };
    flaeche.addEventListener('dragenter', enter);
    flaeche.addEventListener('dragover', over);
    flaeche.addEventListener('dragleave', leave);
    flaeche.addEventListener('drop', drop);
    return () => {
      flaeche.removeEventListener('dragenter', enter);
      flaeche.removeEventListener('dragover', over);
      flaeche.removeEventListener('dragleave', leave);
      flaeche.removeEventListener('drop', drop);
    };
  }, [darfAblegen, task.id]);

  // Beteiligte wie in einer WhatsApp-Gruppe: Zuständige und alle, die geschrieben haben
  const participants = useMemo(() => {
    const ids = new Set<string>(task.assigneeIds);
    for (const m of messages) if (m.authorKind === 'user' && m.authorId) ids.add(m.authorId);
    return [...ids].map((id) => me.members.find((x) => x.userId === id)).filter((x): x is Members[number] => !!x);
  }, [messages, task.assigneeIds, me.members]);

  // Tagesüberschriften und Avatare in einem Durchlauf bestimmen
  const rows = useMemo(() => {
    let day = '';
    let lastAuthor: string | null = null;
    return messages.map((m) => {
      const d = dayLabel(m.createdAt);
      const newDay = d !== day;
      day = d;
      const author = m.kind === 'system' || m.kind === 'meilenstein' ? null : `${m.authorId}:${m.authorName}`;
      const showAvatar = !!author && (author !== lastAuthor || newDay);
      lastAuthor = author;
      return { m, newDay, showAvatar };
    });
  }, [messages]);

  return (
    <>
      <header className="chead">
        {onBack && (
          <button className={`iconbtn plain ${guest ? '' : 'min-[821px]:hidden'}`} onClick={onBack} aria-label="Zurück zur Liste"><Icon name="back" /></button>
        )}
        <Avatar
          name={initials(project?.name ?? task.title)}
          gradient={avatarGradient(project?.id ?? task.id)}
          src={imageUrl(project?.imageMediaId)}
          size={40}
          square
        />
        <div className="min-w-0 flex-1">
          {guest ? (
            <b className="block truncate text-[15.5px]">{task.title}</b>
          ) : (
            <InlineTitle key={task.id} value={task.title} onSave={(title) => void client.saveTask({ id: task.id, projectId: task.projectId, title })} />
          )}
          <span className="flex min-w-0 items-center gap-1 text-xs text-[var(--muted)]">
            {severalBusinesses && <>{business?.name}<Icon name="chev" size={12} /></>}{project?.name}
            {parentTask && (
              <>
                <Icon name="chev" size={12} />
                {onOpenTask ? (
                  <button className="truncate hover:text-[var(--text)] hover:underline" onClick={() => onOpenTask(parentTask.id)} data-tip="Hauptaufgabe öffnen" data-tip-below="">
                    {parentTask.title}
                  </button>
                ) : (
                  <span className="truncate">{parentTask.title}</span>
                )}
              </>
            )}
          </span>
        </div>
        {participants.length > 0 && (
          <div
            className="hidden items-center min-[560px]:flex"
            data-tip={participants.map((x) => x.name).join(', ')}
            data-tip-below=""
            data-tip-wrap=""
            aria-label={`Beteiligt: ${participants.map((x) => x.name).join(', ')}`}
          >
            {participants.slice(0, 4).map((x, i) => (
              <span key={x.userId} className="rounded-full border-2 border-[var(--bg)]" style={{ marginLeft: i ? -10 : 0 }}>
                {memberAvatar(me.members, x.userId, x.name, 28)}
              </span>
            ))}
            {participants.length > 4 && <span className="ml-1 text-xs font-semibold text-[var(--muted)]">+{participants.length - 4}</span>}
          </div>
        )}
        <button
          className="iconbtn"
          aria-pressed={sucheOffen}
          onClick={() => (setSucheOffen((v) => !v), setSuche(''))}
          data-tip="Suchen"
          data-tip-below=""
          aria-label="In dieser Aufgabe suchen"
        >
          <Icon name="search" size={19} />
        </button>
        {!guest && <>
        <button className="iconbtn" onClick={() => (!done || plan.more('openTasks')) && void client.saveTask({ id: task.id, projectId: task.projectId, title: task.title, status: done ? 'offen' : 'erledigt' })}
          data-tip={done ? 'Wieder öffnen' : 'Erledigt'} data-tip-below="" aria-label={done ? 'Wieder öffnen' : 'Als erledigt markieren'}>
          <Icon name={done ? 'archive' : 'check'} size={19} />
        </button>
        </>}
        {/* Ganz rechts: die Angaben zur Aufgabe aus- und einklappen */}
        <button
          className="iconbtn"
          aria-pressed={detailOpen}
          onClick={onToggleDetail}
          data-tip={detailOpen ? 'Angaben einklappen' : 'Angaben ausklappen'}
          data-tip-below=""
          aria-label={detailOpen ? 'Angaben zur Aufgabe einklappen' : 'Angaben zur Aufgabe ausklappen'}
        >
          <Icon name="panel" size={19} />
        </button>
      </header>

      <div className="stepbar">
        <span className={`tag t-${done ? 'erledigt' : 'offen'}`}>{statusText(task.status)}</span>
      </div>

      {sucheOffen && (
        <div className="tasksearch">
          <label className="tsearch">
            <Icon name="search" size={14} />
            <input
              autoFocus
              value={suche}
              onChange={(e) => setSuche(e.target.value)}
              placeholder="In Nachrichten, Sprache und Videos suchen"
              aria-label="In dieser Aufgabe suchen"
              onKeyDown={(e) => e.key === 'Escape' && (setSucheOffen(false), setSuche(''))}
            />
            <button onClick={() => (setSucheOffen(false), setSuche(''))} aria-label="Suche schließen"><Icon name="x" size={14} /></button>
          </label>
          {suche.trim() && (
            <div className="hits scroll">
              {treffer.length === 0 ? (
                <p className="m-0 px-2 py-3 text-center text-[13px] text-[var(--muted)]">Nichts gefunden</p>
              ) : (
                treffer.map((t) => (
                  <button key={`${t.id}-${t.woher}`} onClick={() => zeige(t.id)}>
                    <Icon name={t.woher === 'transkript' ? 'wave' : 'chats'} size={14} className="flex-none text-[var(--brand-a)]" />
                    <span className="min-w-0 flex-1 truncate"><b>{t.wer}:</b> {t.text}</span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      )}

      <div className="scroll chat" ref={scroller} onScroll={(e) => {
        const el = e.currentTarget;
        stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
      }}>
        {messages.length === 0 && (
          <div className="mx-auto mt-10 max-w-[360px] text-center text-[var(--muted)]">
            Noch keine Nachrichten. Schreib, worum es geht — mit Text, Foto, Video oder Sprache.
          </div>
        )}
        {rows.map(({ m, newDay, showAvatar }) => (
          <Row key={m.id} m={m} newDay={newDay} showAvatar={showAvatar} onReply={setReply} onEdit={(x) => (setReply(null), setEditing(x))} />
        ))}
      </div>

      {guest && !guest.canWrite ? (
        <div className="readonly-note"><Icon name="eye" size={16} />Du kannst diese Aufgabe nur ansehen.</div>
      ) : (
        <Composer task={task} reply={reply} onClearReply={() => setReply(null)} editing={editing} onClearEdit={() => setEditing(null)} />
      )}

      {ziehen && (
        <div className="dropzone" aria-hidden="true">
          <div>
            <Icon name="clip" size={30} />
            <b>Hier ablegen</b>
            <small>Dateien kommen in „{task.title}"</small>
          </div>
        </div>
      )}
      {abgelegt && <DropDialog key={abgelegt.length + abgelegt[0]!.name} task={task} files={abgelegt} onClose={() => setAbgelegt(null)} />}
    </>
  );
}

function Row({ m, newDay, showAvatar, onReply, onEdit }: { m: Message; newDay: boolean; showAvatar: boolean; onReply: (r: ReplyTarget) => void; onEdit: (m: Message) => void }) {
  const { me } = useSync();
  const incoming = m.authorId !== me.user.id;
  return (
    <Fragment>
      {newDay && <div className="day"><span>{dayLabel(m.createdAt)}</span></div>}
      {m.kind === 'system' ? (
        <div className="sys" data-msg={m.id}><span>{m.body}</span></div>
      ) : m.kind === 'meilenstein' ? (
        <div className="mile" data-msg={m.id}>
          <span className="fl"><Icon name="flag" size={13} /></span>
          <b className="truncate text-[13px]">{m.body}</b>
          <small className="flex-none text-[12px] text-[var(--muted)]">{m.authorName}</small>
        </div>
      ) : incoming ? (
        <div className="flex items-start gap-2">
          <span className="mt-1.5 w-[30px] flex-none">{showAvatar && memberAvatar(me.members, m.authorId, m.authorName, 30)}</span>
          <MessageBubble m={m} onReply={onReply} onEdit={onEdit} showName={showAvatar} />
        </div>
      ) : (
        <MessageBubble m={m} onReply={onReply} onEdit={onEdit} showName={false} />
      )}
    </Fragment>
  );
}

/**
 * Titel der Aufgabe direkt im Kopf ändern: Klick macht ihn zum Eingabefeld,
 * Enter oder Wegklicken speichert, Escape verwirft.
 */
function InlineTitle({ value, onSave }: { value: string; onSave: (title: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);
  useEffect(() => {
    if (editing) input.current?.select();
  }, [editing]);

  const commit = () => {
    const title = draft.trim().slice(0, 200);
    setEditing(false);
    if (title && title !== value) onSave(title);
    else setDraft(value);
  };

  if (!editing) {
    return (
      <button className="inlinetitle" onClick={() => setEditing(true)} data-tip="Titel ändern" data-tip-below="">
        <b className="block truncate text-[15.5px]">{value}</b>
      </button>
    );
  }
  return (
    <input
      ref={input}
      className="inlinetitle-input"
      value={draft}
      maxLength={200}
      aria-label="Titel der Aufgabe"
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.preventDefault(), commit());
        if (e.key === 'Escape') (e.preventDefault(), e.stopPropagation(), setDraft(value), setEditing(false));
      }}
    />
  );
}
